import type { useTranslations } from 'next-intl'

import type { Messages } from '@/lib/i18n/messages'
import { categories } from '@/lib/site/blog-types'

/** `useTranslations('blogPage')`, or `getTranslations('blogPage')` on the server. */
export type BlogTranslator = ReturnType<typeof useTranslations<'blogPage'>>

type BlogCategoryKey = keyof Messages['blogPage']['categories']

/** Fails to compile unless it names every category the catalog labels, and only those. */
const LABELED_CATEGORIES = {
  comparison: true,
  guide: true,
  strategy: true,
  tips: true,
  data: true,
  news: true,
  report: true,
} satisfies Record<BlogCategoryKey, true>

function isLabeledCategory(category: string): category is BlogCategoryKey {
  return Object.hasOwn(LABELED_CATEGORIES, category)
}

/**
 * A post category's label in the reader's language: the filter pills on
 * `/blog`, the timeline card's badge and the post's eyebrow. `null` for a
 * category `lib/site/blog-types.ts` does not list, which the pages leave
 * out; one it lists that the catalog does not label yet keeps its English
 * label.
 */
export function blogCategoryLabel(
  t: BlogTranslator,
  category: string | undefined
): string | null {
  if (!(category && Object.hasOwn(categories, category))) {
    return null
  }
  return isLabeledCategory(category)
    ? t(`categories.${category}`)
    : categories[category].label
}
